import { Airplane, AirplaneInFlight, Bus, Train } from '@phosphor-icons/react';
import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { useDirection, useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { duration as durationUtil } from '@eti/utils';
import { CarrierLogo } from '@eti/components';
import type { ReactNode } from 'react';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import { useBrandedFareName } from '../../../../../../common/custom-hooks/useBrandedFareName';
import type { TripSegment } from '../../../../../../widgets/trip-itinerary/models';
import TimeDuration from '../../../../../../common/time/TimeDuration';

const airplaneStyles = css`
  transform: rotate(180deg);
`;

const carrierLogoInfoStyles = css`
  height: 20px;
  width: 20px;
`;

const stackStyles = css`
  padding: 8px 0 0;
`;

const VehicleCode = {
  AIR_EQUIPMENT_CODE: 'AIR',
  BUS_EQUIPMENT_CODE: 'BUS',
  TRAIN_EQUIPMENT_CODE: 'TRN',
} as const;

type VehicleCodeType = (typeof VehicleCode)[keyof typeof VehicleCode];

interface ItineraryTripSegmentInfoProps {
  segment: TripSegment;
}

const ItineraryTripSegmentInfo = ({ segment }: ItineraryTripSegmentInfoProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { language, timeFormat } = useSiteContext();
  const { isRTL } = useDirection();
  const { brandedFareInformation, marketingCarrier } = segment;
  const brandedFareName = useBrandedFareName({ brandedFareInformation, marketingCarrier });

  const isTechnicalStopDetailsEnabled = p('IbeClient.TripDetails.TechnicalStops.Enabled');

  const EQUIPMENT_CODE_TO_ICON: Record<VehicleCodeType, ReactNode> = {
    [VehicleCode.AIR_EQUIPMENT_CODE]: (
      <Airplane
        aria-hidden
        className={airplaneStyles}
        color={vars.colors.decoration.primary}
        data-testid="itinerary-trip-segment-info-airplane"
        size={21}
        weight="fill"
      />
    ),
    [VehicleCode.BUS_EQUIPMENT_CODE]: (
      <Bus
        aria-hidden
        color={vars.colors.decoration.primary}
        data-testid="itinerary-trip-segment-info-bus"
        size={21}
        weight="fill"
      />
    ),
    [VehicleCode.TRAIN_EQUIPMENT_CODE]: (
      <Train
        aria-hidden
        color={vars.colors.decoration.primary}
        data-testid="itinerary-trip-segment-info-train"
        size={21}
        weight="fill"
      />
    ),
  };

  const EQUIPMENT_CODE_TO_FLIGHT_NUMBER_TEXT_KEY: Record<
    (typeof VehicleCode)[keyof typeof VehicleCode],
    string
  > = {
    [VehicleCode.AIR_EQUIPMENT_CODE]: 'TripDetails.Details.FlightNumber',
    [VehicleCode.BUS_EQUIPMENT_CODE]: 'TripDetails.Details.BusNumber',
    [VehicleCode.TRAIN_EQUIPMENT_CODE]: 'TripDetails.Details.TrainNumber',
  };

  const vehicleIcon =
    EQUIPMENT_CODE_TO_ICON[segment.equipmentCode as VehicleCodeType] ||
    EQUIPMENT_CODE_TO_ICON[VehicleCode.AIR_EQUIPMENT_CODE];

  const flightNumberTextKey =
    EQUIPMENT_CODE_TO_FLIGHT_NUMBER_TEXT_KEY[
      segment.equipmentCode as (typeof VehicleCode)[keyof typeof VehicleCode]
    ] || EQUIPMENT_CODE_TO_FLIGHT_NUMBER_TEXT_KEY[VehicleCode.AIR_EQUIPMENT_CODE];

  const formattedDuration = durationUtil.getDuration(
    segment.duration ?? 0,
    language.code,
    timeFormat,
  );

  return (
    <TimelineRow data-testid="itinerary-trip-segment-info">
      <TimelineWrapper
        borderColorAfter={vars.colors.decoration.primary}
        borderColorBefore={vars.colors.decoration.primary}
        borderStyleAfter="solid"
        borderStyleBefore="solid"
        childSize={20}
      >
        {vehicleIcon}
      </TimelineWrapper>
      <Stack className={stackStyles} spacing={8}>
        <Inline alignY="center" spacing={8}>
          <Text
            data-testid="itinerary-trip-segment-info-flight-number"
            variant={textVariants._16_BOLD}
          >
            {t(flightNumberTextKey, segment.flightNumber)}
          </Text>
          <Text variant={textVariants._16}>
            <TimeDuration
              data-testid="itinerary-trip-segment-info-duration"
              durationInMs={segment.duration ?? 0}
              screenReaderTextKey="ScreenReader.TripDetails.Details.Duration"
            >
              ({formattedDuration})
            </TimeDuration>
          </Text>
        </Inline>

        {segment.marketingCarrier.code && segment.marketingCarrier.name && (
          <Inline alignY="center" noWrap spacing={8}>
            <CarrierLogo
              aria-hidden
              className={carrierLogoInfoStyles}
              id={segment.marketingCarrier.code}
              imageAlt={segment.marketingCarrier.name}
            />

            <Text data-testid="itinerary-trip-segment-info-marketing-carrier">
              {segment.marketingCarrier.name}
            </Text>
          </Inline>
        )}
        {segment.operatingCarrier.code &&
          segment.operatingCarrier.name &&
          segment.operatingCarrier.name !== segment.marketingCarrier.name && (
            <Inline alignY="center" noWrap spacing={8}>
              <CarrierLogo
                aria-hidden
                className={carrierLogoInfoStyles}
                id={segment.operatingCarrier.code}
                imageAlt={segment.operatingCarrier.name}
              />
              <Text data-testid="itinerary-trip-segment-info-operating-carrier">
                {t('TripDetails.OperatingCarrier')} {segment.operatingCarrier.name}
              </Text>
            </Inline>
          )}
        <div>
          <Text data-testid="itinerary-trip-segment-info-cabin-class">
            {t(
              'TripDetails.Details.CabinClass',
              t(`IbeClient.CabinClass.${segment.cabinClassName}.Text`),
            )}
          </Text>
          {brandedFareName && (
            <Text data-testid="itinerary-trip-segment-info-fare">
              , {t('TripDetails.Details.Fare', brandedFareName)}
            </Text>
          )}
        </div>
        {!isTechnicalStopDetailsEnabled && segment.numberOfTechnicalStops !== 0 && (
          <Inline
            alignY="center"
            data-testid="itinerary-trip-segment-info-technical-stop"
            noWrap
            spacing={8}
          >
            <AirplaneInFlight aria-hidden mirrored={isRTL} size={20} />
            <Text>{t('CartTripDetails.TechnicalStop')}</Text>
          </Inline>
        )}
      </Stack>
    </TimelineRow>
  );
};

export default ItineraryTripSegmentInfo;
